// File: apps/web/src/routes/signup_.authenticate.tsx
// Version: 2026.10.2 | Path: apps/web/src/routes/signup_.authenticate.tsx
//
// 2026.10.2: Creating an account says so: the main button reads "Create your account" when the page is for one,
// "Continue to secure sign-in" only when signing in. Microsoft answers prompt=create with its sign-up page only when the
// sign-in tenant has a guest self-service sign-up user flow with ForgeTrack's app in it; without one it shows plain
// sign-in, which is why no account could be created after the move off External ID (2026-10-17).
//
// 2026.10.1: No countdown: the page waits for Continue. Its three-second hand-off to Microsoft left no time to choose,
// and someone on the sign-in page who meant to create an account had no way to; sign-in now offers "New to ForgeTrack?
// Create an account" (the plan picker, where an account begins) and sign-up "Already have an account? Sign in".
//
// The "bridge" page between plan selection and the Entra hosted auth UI.
// Purposes:
//   1. Confirm the user's plan choice one last time (no surprise after auth).
//   2. Give them a branded moment before Microsoft takes over — continuity.
//   3. Carry the plan selection through MSAL's `state` parameter so it
//      survives the redirect round-trip without needing sessionStorage.
//
// Route: /signup/authenticate?plan=<planId>&cycle=<monthly|annual>
//
// Underscore-prefixed filename (`signup_.authenticate.tsx`) is TanStack's
// convention for a "flat" sibling route that doesn"t nest under /signup"s
// component — we want this to be a full-page takeover, not a child.
//
// State handoff: we pass the plan selection via `login({ state: {...} })`.
// The updated @forgetrack/auth provider awaits handleRedirectPromise() and
// exposes the parsed state as `useAuth().redirectState`, which the /signup
// route reads on mount.

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'motion/react';
import { useAuth } from '@forgetrack/auth';
import { PLANS, type PlanId, type BillingCycle } from '../pages/signup/plans';

// ─── Route ──────────────────────────────────────────────────────────────────

export interface AuthenticateSearch {
  plan?: PlanId;              // still optional — signin mode has no plan
  cycle: BillingCycle;         // always defaulted in validateSearch
  mode: 'signup' | 'signin';   // always defaulted in validateSearch
}

export const Route = createFileRoute('/signup_/authenticate')({
  validateSearch: (search: Record<string, unknown>): AuthenticateSearch => ({
    plan: search.plan as PlanId | undefined,
    cycle: (search.cycle === 'annual' ? 'annual' : 'monthly') as BillingCycle,
    mode: (search.mode === 'signin' ? 'signin' : 'signup') as 'signup' | 'signin',
  }),
  component: AuthenticatePage,
});

// ─── The envelope we round-trip through MSAL state ─────────────────────────

export interface SignupStatePayload {
  v: 1;
  plan: PlanId;
  cycle: BillingCycle;
  mode: 'signup' | 'signin';
}

// ─── Component ──────────────────────────────────────────────────────────────

function AuthenticatePage() {
  const { plan: planId, cycle, mode } = useSearch({ from: '/signup_/authenticate' });
  const navigate = useNavigate();
  const { isInitialized, isAuthenticated, login } = useAuth();

  const plan = useMemo(() => PLANS.find((p) => p.id === planId), [planId]);

  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Guard: no plan selected → send back to plan picker ─────────────────────
  // (Sign-in mode is allowed without a plan — they're existing users.)
  useEffect(() => {
    if (mode === 'signup' && !plan) {
      navigate({ to: '/signup', replace: true });
    }
  }, [plan, mode, navigate]);

  // ── Guard: already authenticated → skip the bridge, go straight to /signup
  //    so the post-auth flow (bootstrap → workspace → provision) takes over.
  useEffect(() => {
    if (!isInitialized) return;
    if (isAuthenticated) {
      navigate({ to: '/signup', replace: true });
    }
  }, [isInitialized, isAuthenticated, navigate]);

  // ── Fire the MSAL redirect with the plan encoded in state ──────────────────
  const launchAuth = useCallback(async () => {
    if (!isInitialized) return;
    if (mode === 'signup' && !plan) return;

    setRedirecting(true);
    setError(null);
    try {
      const statePayload: SignupStatePayload = {
        v: 1,
        plan: (plan?.id ?? 'free') as PlanId,
        cycle,
        mode,
      };

      // The updated @forgetrack/auth login() takes an options object with
      // { prompt, state }. State is JSON.stringify'd internally and comes
      // back on the other side as useAuth().redirectState.
      await login({
        prompt: mode === 'signup' ? 'create' : 'login',
        state: statePayload,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to open secure sign-in');
      setRedirecting(false);
    }
  }, [isInitialized, mode, plan, cycle, login]);

  // No automatic hand-off: the page waits for Continue, so there is time to change the plan, or to switch between
  // signing in and creating an account.

  if (mode === 'signup' && !plan) return null; // navigating away

  const priceDisplay = plan
    ? plan.monthlyPrice < 0
      ? 'Custom pricing'
      : plan.monthlyPrice === 0
      ? 'Free forever'
      : cycle === 'monthly'
      ? `$${plan.monthlyPrice}/month`
      : `$${plan.annualPrice}/year`
    : '';

  const modeLabel = mode === 'signup' ? 'Create your account' : 'Sign in';
  const eyebrow = mode === 'signup' ? 'STEP 2 OF 3' : 'SECURE SIGN-IN';

  return (
    <main className="ft-bridge">
      <div className="ft-bridge__bg" aria-hidden />
      <div className="ft-bridge__grid" aria-hidden />
      <div className="ft-bridge__glow" aria-hidden />

      <motion.div
        className="ft-bridge__brand"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <ForgeTrackMark />
      </motion.div>

      <motion.div
        className="ft-bridge__card"
        initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
      >
        <motion.div
          className="ft-bridge__eyebrow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
        >
          {eyebrow}
        </motion.div>

        <motion.h1
          className="ft-bridge__title"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.42 }}
        >
          {modeLabel}
        </motion.h1>

        <motion.p
          className="ft-bridge__subtitle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          You'll be handed off to our secure identity provider to{' '}
          {mode === 'signup' ? 'create your account' : 'sign in'}. When you return,
          your workspace will be ready.
        </motion.p>

        {mode === 'signup' && plan && (
          <motion.div
            className="ft-bridge__plan"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.58 }}
          >
            <div className="ft-bridge__plan-label">SELECTED PLAN</div>
            <div className="ft-bridge__plan-row">
              <div className="ft-bridge__plan-name">{plan.name}</div>
              <div className="ft-bridge__plan-price">{priceDisplay}</div>
            </div>
            <div className="ft-bridge__plan-meta">
              {plan.tagline} · {cycle === 'monthly' ? 'Billed monthly' : 'Billed annually'}
            </div>
          </motion.div>
        )}

        <motion.div
          className="ft-bridge__actions"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.68 }}
        >
          <button
            className="ft-bridge__btn ft-bridge__btn--primary"
            onClick={launchAuth}
            disabled={!isInitialized || redirecting}
          >
            {redirecting ? (
              <>
                <span className="ft-bridge__spinner" aria-hidden />
                <span>{mode === 'signup' ? 'Opening secure sign-up…' : 'Opening secure sign-in…'}</span>
              </>
            ) : (
              <>
                <span>{mode === 'signup' ? 'Create your account' : 'Continue to secure sign-in'}</span>
                <span className="ft-bridge__arrow">→</span>
              </>
            )}
          </button>

          {!redirecting && mode === 'signup' && (
            <button
              className="ft-bridge__btn ft-bridge__btn--ghost"
              onClick={() => navigate({ to: '/signup' })}
            >
              ← Change plan
            </button>
          )}

          {!redirecting && (
            <button
              className="ft-bridge__btn ft-bridge__btn--ghost"
              onClick={() =>
                mode === 'signup'
                  ? window.location.assign('/signup/authenticate?mode=signin')
                  : navigate({ to: '/signup' })
              }
            >
              {mode === 'signup' ? 'Already have an account? Sign in' : 'New to ForgeTrack? Create an account'}
            </button>
          )}
        </motion.div>

        {error && (
          <motion.div
            className="ft-bridge__error"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {error}
          </motion.div>
        )}

        <motion.div
          className="ft-bridge__trust"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.82 }}
        >
          <LockIcon />
          <span>Secured by Microsoft Entra External ID · SOC 2 compliant</span>
        </motion.div>
      </motion.div>

      <style>{BRIDGE_CSS}</style>
    </main>
  );
}

// ─── Visual bits ────────────────────────────────────────────────────────────

function ForgeTrackMark() {
  return (
    <div className="ft-bridge__wordmark">
      <svg viewBox="0 0 24 24" aria-hidden>
        <path d="M4 21 L12 3 L20 21 L16 21 L12 12 L8 21 Z" fill="currentColor" />
      </svg>
      <span>
        <span className="ft-bridge__wordmark-forge">Forge</span>
        <span className="ft-bridge__wordmark-track">Track</span>
      </span>
    </div>
  );
}

function LockIcon() {
  return (
    <svg width="12" height="14" viewBox="0 0 12 14" fill="none" aria-hidden>
      <rect x="1.5" y="6" width="9" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M3.5 6V4a2.5 2.5 0 015 0v2" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────
// Matches the copper-glow aesthetic of the Entra branding background so the
// visual handoff from this page → Microsoft hosted page feels continuous.

const BRIDGE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

.ft-bridge {
  --ft-ink:      #0A0E1A;
  --ft-ink-2:    #050910;
  --ft-copper:   #A0522D;
  --ft-copper-2: #B8623A;
  --ft-paper:    #F4F1EA;
  --ft-dim:      rgba(244, 241, 234, 0.58);
  --ft-faint:    rgba(244, 241, 234, 0.12);
  --ft-line:     rgba(244, 241, 234, 0.08);

  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 48px;
  padding: 24px;
  background: var(--ft-ink-2);
  color: var(--ft-paper);
  font-family: 'Space Grotesk', system-ui, sans-serif;
  overflow: hidden;
}

.ft-bridge__bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #0A0E1A 0%, #050910 50%, #020408 100%);
  pointer-events: none;
}

.ft-bridge__grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(to right, rgba(160, 82, 45, 0.04) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(160, 82, 45, 0.04) 1px, transparent 1px);
  background-size: 96px 96px;
  mask-image: radial-gradient(ellipse 80% 70% at 30% 45%, black, transparent 70%);
  -webkit-mask-image: radial-gradient(ellipse 80% 70% at 30% 45%, black, transparent 70%);
  pointer-events: none;
}

.ft-bridge__glow {
  position: absolute;
  top: 30%;
  left: 18%;
  width: 900px;
  height: 900px;
  background: radial-gradient(
    circle,
    rgba(160, 82, 45, 0.28) 0%,
    rgba(160, 82, 45, 0.12) 25%,
    rgba(160, 82, 45, 0.04) 45%,
    transparent 65%
  );
  filter: blur(60px);
  transform: translate(-50%, -50%);
  animation: ft-bridge-drift 18s ease-in-out infinite;
  pointer-events: none;
}
.ft-bridge__glow::after {
  content: '';
  position: absolute;
  bottom: -200px;
  right: -300px;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(34, 90, 160, 0.18), transparent 70%);
  filter: blur(50px);
}
@keyframes ft-bridge-drift {
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50%      { transform: translate(-48%, -52%) scale(1.06); }
}

.ft-bridge__brand { position: relative; z-index: 2; }

.ft-bridge__wordmark {
  display: inline-flex;
  align-items: baseline;
  gap: 0.3rem;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 1.6rem;
  letter-spacing: -0.035em;
  line-height: 1;
}
.ft-bridge__wordmark svg {
  width: 0.9em;
  height: 0.9em;
  color: var(--ft-copper);
  transform: translateY(0.08em);
  filter: drop-shadow(0 0 12px rgba(160, 82, 45, 0.5));
}
.ft-bridge__wordmark-forge { color: var(--ft-copper); }
.ft-bridge__wordmark-track { color: var(--ft-paper); margin-left: -0.025em; }

.ft-bridge__card {
  position: relative;
  z-index: 2;
  width: min(520px, 92vw);
  padding: 44px 40px 36px;
  background: rgba(10, 14, 26, 0.72);
  border: 1px solid var(--ft-faint);
  border-radius: 18px;
  backdrop-filter: blur(24px) saturate(1.4);
  -webkit-backdrop-filter: blur(24px) saturate(1.4);
  box-shadow:
    0 0 0 1px rgba(160, 82, 45, 0.08),
    0 24px 80px -20px rgba(0, 0, 0, 0.8),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
}
.ft-bridge__card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 18px;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(160, 82, 45, 0.4) 0%,
    transparent 35%,
    transparent 65%,
    rgba(184, 98, 58, 0.25) 100%
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
  pointer-events: none;
  opacity: 0.7;
}

.ft-bridge__eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ft-copper-2);
  margin-bottom: 14px;
  display: inline-flex;
  align-items: center;
}
.ft-bridge__eyebrow::before {
  content: '';
  display: inline-block;
  width: 1.4rem;
  height: 1px;
  background: currentColor;
  margin-right: 0.6rem;
  opacity: 0.6;
}

.ft-bridge__title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 30px;
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1.1;
  margin: 0 0 10px;
  color: var(--ft-paper);
}

.ft-bridge__subtitle {
  font-size: 14px;
  line-height: 1.6;
  color: var(--ft-dim);
  margin: 0 0 24px;
}

.ft-bridge__plan {
  position: relative;
  padding: 18px 20px;
  background: rgba(160, 82, 45, 0.06);
  border: 1px solid rgba(160, 82, 45, 0.2);
  border-radius: 12px;
  margin-bottom: 24px;
}
.ft-bridge__plan::before {
  content: '';
  position: absolute;
  left: 0;
  top: 16px;
  bottom: 16px;
  width: 3px;
  background: linear-gradient(180deg, var(--ft-copper), var(--ft-copper-2));
  border-radius: 0 2px 2px 0;
}
.ft-bridge__plan-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  letter-spacing: 0.2em;
  color: var(--ft-copper-2);
  margin-bottom: 6px;
}
.ft-bridge__plan-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}
.ft-bridge__plan-name {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 19px;
  font-weight: 600;
  color: var(--ft-paper);
  letter-spacing: -0.01em;
}
.ft-bridge__plan-price {
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  font-weight: 500;
  color: var(--ft-copper-2);
}
.ft-bridge__plan-meta {
  font-size: 12px;
  color: var(--ft-dim);
}

.ft-bridge__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}

.ft-bridge__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 15px 20px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.005em;
  border-radius: 11px;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.25s, opacity 0.2s, background 0.2s, border-color 0.2s;
  border: none;
}

.ft-bridge__btn--primary {
  background: linear-gradient(135deg, var(--ft-copper) 0%, var(--ft-copper-2) 100%);
  color: var(--ft-paper);
  box-shadow:
    0 8px 24px -8px rgba(160, 82, 45, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}
.ft-bridge__btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow:
    0 12px 32px -8px rgba(160, 82, 45, 0.7),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}
.ft-bridge__btn--primary:active:not(:disabled) { transform: translateY(0); }
.ft-bridge__btn--primary:disabled { opacity: 0.65; cursor: wait; }

.ft-bridge__btn--ghost {
  background: transparent;
  color: var(--ft-dim);
  border: 1px solid var(--ft-faint);
  font-size: 13px;
  padding: 11px 20px;
}
.ft-bridge__btn--ghost:hover {
  background: rgba(244, 241, 234, 0.04);
  color: var(--ft-paper);
  border-color: rgba(244, 241, 234, 0.2);
}

.ft-bridge__arrow {
  font-size: 18px;
  transition: transform 0.2s;
}
.ft-bridge__btn--primary:hover:not(:disabled) .ft-bridge__arrow { transform: translateX(3px); }

.ft-bridge__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(244, 241, 234, 0.25);
  border-top-color: var(--ft-paper);
  border-radius: 50%;
  animation: ft-bridge-spin 0.7s linear infinite;
}
@keyframes ft-bridge-spin { to { transform: rotate(360deg); } }

.ft-bridge__error {
  padding: 10px 14px;
  margin-bottom: 14px;
  background: rgba(248, 113, 113, 0.08);
  border: 1px solid rgba(248, 113, 113, 0.25);
  border-radius: 8px;
  color: #f87171;
  font-size: 13px;
  font-family: 'JetBrains Mono', monospace;
}

.ft-bridge__trust {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--ft-line);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.1em;
  color: var(--ft-dim);
  text-transform: uppercase;
}
.ft-bridge__trust svg { color: var(--ft-copper-2); }

@media (prefers-reduced-motion: reduce) {
  .ft-bridge__glow { animation: none; }
  .ft-bridge__spinner { animation-duration: 1.5s; }
}
`;
